import React, { useState, useEffect, useMemo } from 'react';
import { FaSave, FaCamera, FaUser, FaSyncAlt, FaCreditCard, FaFileInvoice, FaArrowRight } from 'react-icons/fa';
import { doc, getDoc, updateDoc, serverTimestamp, collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db, storage } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useSidebar } from '../../context/SidebarContext';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
 
const menuItems = [
  { id: 'profile', title: 'Profile', icon: FaUser },
  { id: 'subscription', title: 'Subscription', icon: FaSyncAlt },
  { id: 'billing', title: 'Billing', icon: FaCreditCard },
  { id: 'invoices', title: 'Invoices', icon: FaFileInvoice },
];

const defaultBillingAddress = {
  line1: '',
  line2: '',
  city: '',
  state: '',
  postalCode: '',
  country: 'India', // default to India
};

const sanitizePhoneNumber = (value = '') => value.toString().replace(/\D/g, '').slice(0, 10);
const normalizePhoneDigits = (value = '', countryCode = '+91') => {
  const digits = value?.toString().replace(/\D/g, '') || '';
  const codeDigits = (countryCode || '+91').replace('+', '');
  if (digits.startsWith(codeDigits) && digits.length > codeDigits.length) {
    return sanitizePhoneNumber(digits.slice(codeDigits.length));
  }
  return sanitizePhoneNumber(digits.length > 10 ? digits.slice(-10) : digits);
};

const formatINR = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '—';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount);
};

const parseToDate = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (typeof value === 'object' && typeof value.seconds === 'number') return new Date(value.seconds * 1000);
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'string') {
    const slashFormat = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = value.match(slashFormat);
    if (match) {
      const [, day, month, year] = match;
      const parsed = new Date(Number(year), Number(month) - 1, Number(day));
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const formatDateLabel = (value) => {
  const date = parseToDate(value);
  return date
    ? date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';
};

const CANCEL_SUBSCRIPTION_API = process.env.REACT_APP_CANCEL_SUBSCRIPTION_API;

const SubscriptionSection = ({ subscriptionInfo, isLoading, limits, limitsLoading, onCancelPlan, cancelLoading, showCancelNotice }) => {
  const planPricing = {
    Lite: { monthly: 299, yearly: 149 },
    Plus: { monthly: 499, yearly: 249 },
  };
  const unlimitedKeys = new Set(['chat', 'summary', 'quiz', 'flashcards', 'notes', 'uploads']);

  const handleManagePlan = () => {
    if (typeof window === 'undefined') return;
    window.location.href = '/plans';
  };

  const formatLimitValue = (value, type, suffix = '') => {
    if (value === null || value === undefined) return '—';
    if (type === 'date') return formatDateLabel(value);
    if (typeof value === 'number') return `${value.toLocaleString('en-IN')}${suffix}`;
    return value;
  };

  const limitFieldConfig = [
    { key: 'chat', label: 'Chats', type: 'number' },
    { key: 'summary', label: 'Summaries', type: 'number' },
    { key: 'quiz', label: 'Quizzes', type: 'number' },
    { key: 'flashcards', label: 'Flashcards', type: 'number' },
    { key: 'notes', label: 'Notes', type: 'number' },
    { key: 'uploads', label: 'Uploads', type: 'number' },
    { key: 'fileUploadLimit', label: 'File upload limit', type: 'number', suffix: ' MB' },
  ];

  const plan = subscriptionInfo.plan || 'Free';
  const isPlusPlan = plan.toLowerCase() === 'plus';

  const fieldsToRender = isPlusPlan
    ? limitFieldConfig
    : [
        ...limitFieldConfig,
        { key: 'resetAt', label: 'Resets at', type: 'date' },
      ];

  if (isLoading) {
    return (
      <div className="flex h-48 items-center justify-center gap-2 text-sm text-gray-500">
        <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
        Loading subscription…
      </div>
    );
  }

  if (!subscriptionInfo) {
    return (
      <div className="space-y-6">
        <header className="rounded-3xl bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 p-6 text-slate-100 shadow-2xl shadow-blue-950/40">
          <h2 className="text-3xl font-semibold">Subscription</h2>
          <p className="text-sm text-blue-100/80">No active subscription found. Upgrade to unlock premium tooling.</p>
        </header>
        <section className="rounded-3xl border border-blue-100 bg-white p-6 text-gray-600 shadow-sm">
          <p>We couldn’t locate any subscription data for this account. Visit the plans page to start a subscription.</p>
        </section>
      </div>
    );
  }

  const billingCycle = subscriptionInfo.billingCycle || '';
  const price = planPricing[plan]?.[billingCycle || 'monthly'];
  const priceAmount = plan === 'Free' ? '₹0' : price ? formatINR(price) : '—';
  const priceCaption = plan === 'Free'
    ? 'Always free'
    : billingCycle
      ? `Per ${billingCycle}`
      : 'Per month';
  const models = subscriptionInfo.models || [];
  const renewalDate = parseToDate(subscriptionInfo.renewsOn);
  const { renewalCountdownLabel, renewalWeekdayLabel, renewalDaysRemaining } = (() => {
    if (!renewalDate) return { renewalCountdownLabel: null, renewalWeekdayLabel: null, renewalDaysRemaining: null };
    const diffMs = renewalDate.getTime() - Date.now();
    if (Number.isNaN(diffMs)) return { renewalCountdownLabel: null, renewalWeekdayLabel: null, renewalDaysRemaining: null };
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffMs <= 0) {
      return { renewalCountdownLabel: 'Renewing today', renewalWeekdayLabel: renewalDate.toLocaleDateString('en-IN', { weekday: 'long' }), renewalDaysRemaining: 0 };
    }
    if (days >= 1) {
      return {
        renewalCountdownLabel: `Due in ${days} day${days > 1 ? 's' : ''}`,
        renewalWeekdayLabel: renewalDate.toLocaleDateString('en-IN', { weekday: 'long' }),
        renewalDaysRemaining: days
      };
    }
    const hours = Math.ceil(diffMs / (1000 * 60 * 60));
    return {
      renewalCountdownLabel: `Due in ${hours}h`,
      renewalWeekdayLabel: renewalDate.toLocaleDateString('en-IN', { weekday: 'long' }),
      renewalDaysRemaining: 0
    };
  })();
  const canCancelPlan = typeof renewalDaysRemaining === 'number' ? renewalDaysRemaining <= 7 : false;


  return (
    <div className="space-y-8">
      <header className="rounded-3xl bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 p-6 text-slate-100 shadow-2xl shadow-blue-950/40">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-3xl font-semibold">Subscription</h2>
          </div>
        </div>
      </header>
      <section className="grid gap-6 md:grid-cols-2">
        <article className="relative overflow-hidden rounded-3xl border border-slate-800 bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-6 text-slate-100 shadow-2xl">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.25),_transparent_60%)]" aria-hidden />
          <div className="relative z-10 space-y-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div className="space-y-3">
                <span className="inline-flex items-center gap-2 rounded-full border border-blue-400/50 bg-blue-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.3em] text-blue-100/90">
                  Current plan
                </span>
                <h3 className="text-2xl font-semibold text-slate-50 mt-4">{plan} plan</h3>
                <p className="text-sm text-slate-300">
                  {billingCycle ? `${billingCycle.charAt(0).toUpperCase()}${billingCycle.slice(1)} billing` : 'Billing cycle unavailable'}
                </p>
              </div>
              <div className="rounded-3xl border border-blue-500/40 bg-blue-500/10 px-5 py-4 text-right">
                <span className="block text-3xl font-semibold text-blue-100">{priceAmount}</span>
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-200/80">{priceCaption}</span>
              </div>
            </div>

            <dl className="grid gap-4 text-sm text-slate-200 sm:grid-cols-2">
              <div className="rounded-2xl bg-slate-900/60 p-4">
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-xs uppercase tracking-wide text-slate-400">Next renewal</dt>
                  {renewalCountdownLabel && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-blue-400/40 bg-blue-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-blue-200">
                      {renewalCountdownLabel}
                    </span>
                  )}
                </div>
                <dd className="mt-3 text-xl font-semibold text-slate-50">
                  {formatDateLabel(subscriptionInfo.renewsOn)}
                </dd>
                {renewalWeekdayLabel && (
                  <p className="mt-1 text-xs uppercase tracking-wide text-slate-400">{renewalWeekdayLabel}</p>
                )}
              </div>
              <div className="rounded-2xl bg-slate-900/60 p-4">
                <dt className="text-xs uppercase tracking-wide text-slate-400">Allowed models</dt>
                <dd className="mt-2 flex flex-wrap gap-2">
                  {models.length
                    ? models.map((model) => (
                        <span
                          key={model}
                          className="inline-flex items-center rounded-full border border-blue-400/40 bg-blue-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-blue-100"
                        >
                          {model}
                        </span>
                      ))
                    : <span className="text-xs text-slate-400">—</span>}
                </dd>
              </div>
            </dl>

            <div className="flex flex-wrap gap-3 pt-4">
              <button
                type="button"
                onClick={handleManagePlan}
                className="inline-flex items-center gap-2 rounded-full bg-black px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-slate-900/40 transition hover:translate-y-0.5 hover:bg-black/90 focus:outline-none focus:ring-2 focus:ring-slate-500/60"
              >
                Manage plan
                <FaArrowRight className="text-[10px]" />
              </button>
              <button
                type="button"
                onClick={onCancelPlan}
                disabled={cancelLoading}
                className={`inline-flex items-center gap-2 rounded-full border border-rose-400/50 px-6 py-3 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-rose-300/40 ${
                  canCancelPlan && !cancelLoading
                    ? 'bg-rose-500/10 text-rose-200 hover:border-rose-300 hover:bg-rose-500/20 hover:text-rose-100'
                    : 'cursor-not-allowed bg-slate-800/40 text-slate-500 border-slate-700'
                }`}
              >
                {cancelLoading ? 'Cancelling…' : 'Cancel plan'}
              </button>
            </div>
            {showCancelNotice && (
              <p className="pt-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                Cancellation available in the last 7 days before renewal
              </p>
            )}
            {cancelLoading && (
              <p className="pt-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                Processing cancellation…
              </p>
            )}
          </div>
        </article>

        <article className="rounded-3xl border border-slate-800 bg-slate-950/80 p-6 text-slate-100 shadow-2xl">
          <h3 className="text-lg font-semibold text-slate-50">Usage limits</h3>
          {limitsLoading ? (
            <div className="mt-5 flex items-center gap-2 text-sm text-slate-300">
              <span className="inline-block h-4 w-4 animate-spin rounded-full border border-blue-400 border-t-transparent" />
              Loading limits…
            </div>
          ) : limits ? (
            <dl className="mt-5 grid gap-4 text-sm text-slate-200 sm:grid-cols-2">
              {fieldsToRender.map(({ key, label, type, suffix = '' }) => (
                <div key={key} className="rounded-2xl border border-slate-800/70 bg-slate-900/70 p-4 shadow-inner shadow-slate-950/40">
                  <dt className="text-xs uppercase tracking-wide text-slate-400">{label}</dt>
                  <dd className="mt-2 text-sm font-semibold text-slate-100">
                    {isPlusPlan && unlimitedKeys.has(key)
                      ? 'Unlimited'
                      : formatLimitValue(limits[key], type, suffix)}
                  </dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="mt-5 text-sm text-slate-300">No limits data available.</p>
          )}
        </article>
      </section>
    </div>
  );
};

const BillingSection = ({ address, onFieldChange, onSave, canSave, isSaving }) => {
  const COUNTRIES = useMemo(() => [
    { value: 'India', label: '🇮🇳 India' },
    // ...add more countries later if needed
  ], []);

  const INDIA_STATES_UT = useMemo(() => [
    'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal','Andaman and Nicobar Islands','Chandigarh','Dadra and Nagar Haveli and Daman and Diu','Delhi','Jammu and Kashmir','Ladakh','Lakshadweep','Puducherry'
  ], []);

  const isIndia = (address.country || 'India') === 'India';

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-3xl font-semibold text-black">Billing Information</h2>
      </header>
      <section className="rounded-3xl border border-blue-100 bg-white/90 p-6 shadow-sm shadow-blue-50/60 backdrop-blur">
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="flex flex-col space-y-2 text-sm font-medium text-gray-700">
            <span>Address Line 1</span>
            <input
              type="text"
              value={address.line1 || ''}
              onChange={(e) => onFieldChange('line1', e.target.value)}
              placeholder="House / Flat / Street"
              className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-gray-700 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            />
          </label>
          <label className="flex flex-col space-y-2 text-sm font-medium text-gray-700">
            <span>Address Line 2</span>
            <input
              type="text"
              value={address.line2 || ''}
              onChange={(e) => onFieldChange('line2', e.target.value)}
              placeholder="Apartment / Landmark (optional)"
              className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-gray-700 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            />
          </label>
          <label className="flex flex-col space-y-2 text-sm font-medium text-gray-700">
            <span>City</span>
            <input
              type="text"
              value={address.city || ''}
              onChange={(e) => onFieldChange('city', e.target.value)}
              placeholder="Enter city"
              className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-gray-700 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            />
          </label>
          <label className="flex flex-col space-y-2 text-sm font-medium text-gray-700">
            <span>State / UT</span>
            {isIndia ? (
              <select
                value={address.state || ''}
                onChange={(e) => onFieldChange('state', e.target.value)}
                className="w-full rounded-2xl border border-gray-200 bg-white px-3 py-3 text-gray-700 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              >
                <option value="" disabled>Select state/UT</option>
                {INDIA_STATES_UT.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={address.state || ''}
                onChange={(e) => onFieldChange('state', e.target.value)}
                placeholder="Enter state / province"
                className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-gray-700 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              />
            )}
          </label>
          <label className="flex flex-col space-y-2 text-sm font-medium text-gray-700">
            <span>Postal Code</span>
            <input
              type="text"
              value={address.code || ''}
              onChange={(e) => onFieldChange('code', e.target.value)}
              placeholder="PIN code"
              className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-gray-700 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            />
          </label>
          <label className="flex flex-col space-y-2 text-sm font-medium text-gray-700">
            <span>Country</span>
            <select
              value={address.country || 'India'}
              onChange={(e) => onFieldChange('country', e.target.value)}
              className="w-full rounded-2xl border border-gray-200 bg-white px-3 py-3 text-gray-700 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            >
              {COUNTRIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="mt-6 flex flex-col gap-3 rounded-2xl bg-slate-50 px-4 py-4 text-sm text-gray-600 sm:flex-row sm:items-center sm:justify-between">
          <span>We will use this address for billing purposes.</span>
          <button
            type="button"
            onClick={onSave}
            disabled={!canSave || isSaving}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold text-white shadow-sm transition ${
              !canSave || isSaving ? 'cursor-not-allowed bg-black' : 'bg-blue-700 hover:bg-blue-800'
            }`}
          >
            {isSaving ? 'Saving…' : 'Save address'}
          </button>
        </div>
      </section>
    </div>
  );
};

const InvoicesSection = ({ invoices, isLoading, showAllInvoices, onToggleShowAll }) => {
  const [downloadingId, setDownloadingId] = useState(null);

  const handleDownloadInvoice = async (invoice) => {
    if (!invoice?.url) return;
    const sanitizedInvoiceId = (invoice.invoiceId || `invoice-${invoice.id || ''}`).toString().replace(/[^\w-]+/g, '-');
    const downloadFileName = `${sanitizedInvoiceId || 'invoice'}.pdf`;

    try {
      setDownloadingId(invoice.id);
      const response = await fetch(invoice.url, { mode: 'cors' });
      if (!response.ok) throw new Error('Failed to fetch invoice');
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = downloadFileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('Invoice download failed:', error);
      alert('Unable to download invoice. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-48 items-center justify-center gap-2 text-sm text-gray-500">
        <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
        Loading invoices…
      </div>
    );
  }

  if (!invoices?.length) {
    return (
      <div className="space-y-6">
        <header>
          <h2 className="text-3xl font-semibold text-black">Invoices</h2>
        </header>
        <section className="rounded-3xl border border-blue-100 bg-white p-6 text-gray-600 shadow-sm">
          <p>No invoices found yet. Complete a subscription payment to see tax invoices here.</p>
        </section>
      </div>
    );
  }

  const statusTokens = {
    paid: { bg: 'bg-emerald-100/80', text: 'text-emerald-700', label: 'Paid' },
    pending: { bg: 'bg-amber-100/80', text: 'text-amber-700', label: 'Pending' },
    cancelled: { bg: 'bg-rose-100/80', text: 'text-rose-700', label: 'Cancelled' },
  };
  const visibleInvoices = showAllInvoices ? invoices : invoices.slice(0, 6);

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-3xl font-semibold text-black">Invoices</h2>
        </div>
        <span className="rounded-full bg-blue-50 px-4 py-1 text-xs font-semibold text-blue-700">
          {invoices.length} invoice{invoices.length > 1 ? 's' : ''}
        </span>
      </header>

      <div className="grid gap-4">
        {visibleInvoices.map((invoice) => {
          const statusKey = (invoice.status || 'paid').toLowerCase();
          const statusMeta = statusTokens[statusKey] || statusTokens.paid;
          const periodStartRaw = invoice.periodStart ?? invoice.period_start ?? null;
          const periodEndRaw = invoice.periodEnd ?? invoice.period_end ?? null;
          const issuedLabel = formatDateLabel(invoice.createdAt || periodEndRaw);
          const hasPeriodStart = Boolean(periodStartRaw);
          const hasPeriodEnd = Boolean(periodEndRaw);
          const periodMessage = hasPeriodStart && hasPeriodEnd
            ? `Billing period ${formatDateLabel(periodStartRaw)} → ${formatDateLabel(periodEndRaw)}`
            : hasPeriodStart
              ? `Billing period starts ${formatDateLabel(periodStartRaw)}`
              : hasPeriodEnd
                ? `Billing period ends ${formatDateLabel(periodEndRaw)}`
                : null;

          return (
            <article
              key={invoice.id}
              className="group rounded-3xl border border-slate-200/80 bg-white/95 p-6 shadow-sm shadow-slate-200/50 transition hover:-translate-y-0.5 hover:shadow-lg"
            >
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-slate-500 uppercase tracking-wide">
                    Invoice #{invoice.invoiceId}
                  </p>
                  <h3 className="text-xl font-semibold text-slate-900">{invoice.plan} plan</h3>
                  <p className="text-sm text-slate-500">
                    Issued on {issuedLabel}
                  </p>
                  {periodMessage && (
                    <p className="text-xs text-slate-400">{periodMessage}</p>
                  )}
                </div>

                <div className="flex flex-col items-end gap-3">
                  <span className="text-2xl font-semibold text-slate-900">{formatINR(invoice.amount)}</span>
                  <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${statusMeta.bg} ${statusMeta.text}`}>
                    {statusMeta.label}
                  </span>
                  {invoice.url ? (
                    <button
                      type="button"
                      onClick={() => handleDownloadInvoice(invoice)}
                      disabled={downloadingId === invoice.id}
                      className={`inline-flex items-center gap-2 rounded-full border border-blue-500/30 px-4 py-2 text-xs font-semibold text-white transition ${
                        downloadingId === invoice.id ? 'bg-gray-400 cursor-not-allowed' : 'bg-black hover:bg-black'
                      }`}
                    >
                      {downloadingId === invoice.id ? 'Downloading…' : 'Download PDF'}
                      <FaArrowRight className="text-[10px]" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-100 px-4 py-2 text-xs font-semibold text-slate-400"
                    >
                      Not available
                    </button>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {invoices.length > 6 && (
        <footer className="flex items-center justify-between gap-3 px-4 py-3 text-xs text-slate-500">
          <span>
            Showing {visibleInvoices.length} of {invoices.length} invoices
          </span>
          <button
            type="button"
            onClick={onToggleShowAll}
            className="inline-flex items-center gap-1 rounded-full border border-slate-300 px-3 py-1.5 font-semibold text-slate-700 transition hover:border-slate-400 hover:text-slate-900"
          >
            {showAllInvoices ? 'Show less' : 'Show all'}
          </button>
        </footer>
      )}
    </div>
  );
};

const SettingsPage = () => {
  const { user } = useAuth();
  const { collapsed, isMobile } = useSidebar();
  const [isLoading, setIsLoading] = useState(true);
  const [accountDetails, setAccountDetails] = useState({
    avatar: '',
    fullName: '',
    dob: '',
    email: user?.email || '',
    phoneNumber: '',
    phoneCountryCode: '+91',
  });
  const [isChanged, setIsChanged] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [billingAddress, setBillingAddress] = useState(() => ({ ...defaultBillingAddress }));
  const [originalBilling, setOriginalBilling] = useState(() => ({ ...defaultBillingAddress }));
  const [isSavingBilling, setIsSavingBilling] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [subscriptionLimits, setSubscriptionLimits] = useState(null);
  const [limitsLoading, setLimitsLoading] = useState(true);
  const [isCancellingPlan, setIsCancellingPlan] = useState(false);
  const [showCancelNotice, setShowCancelNotice] = useState(false);
  const [showAllInvoices, setShowAllInvoices] = useState(false);

  const layoutShiftStyle = !collapsed && !isMobile
    ? { marginLeft: '16rem', transition: 'margin-left 0.3s ease' }
    : {};

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) return;
      
      setIsLoading(true);
      try {
        const userDocRef = doc(db, 'users', user.uid);
        
        // Fetch user profile
        const userProfileRef = doc(userDocRef, 'UserProfile', user.uid);
        const userProfileSnapshot = await getDoc(userProfileRef);
        
        // Fetch personal info (only dob is used now)
        const personalInfoRef = doc(userDocRef, 'PersonalInfo', user.uid);
        const personalInfoSnapshot = await getDoc(personalInfoRef);

        if (userProfileSnapshot.exists()) {
          const profileData = userProfileSnapshot.data();
          const storedCountryCode = profileData.phoneCountryCode || '+91';
          const hydratedAddress = {
            line1: profileData.address1 || profileData.line1 || '',
            line2: profileData.address2 || profileData.line2 || '',
            city: profileData.city || '',
            state: profileData.state || '',
            code: profileData.code || profileData.zip || '',
            country: profileData.country || '',
          };
          setBillingAddress(hydratedAddress);
          setOriginalBilling(hydratedAddress);
          setAccountDetails((prev) => ({
            ...prev,
            avatar: profileData.profilePhoto || '',
            fullName: `${profileData.firstName || ''} ${profileData.lastName || ''}`.trim(),
            email: profileData.emailAddress || user.email || '',
            phoneNumber: normalizePhoneDigits(profileData.phoneNumber, storedCountryCode),
            phoneCountryCode: storedCountryCode,
          }));
        }

        if (personalInfoSnapshot.exists()) {
          const personalData = personalInfoSnapshot.data();
          setAccountDetails(prev => ({
            ...prev,
            dob: personalData.dob || ''
          }));
        }

      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      fetchUserData();
    }
  }, [user]);

  useEffect(() => {
    if (isPopupOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = 'auto';
      document.body.style.position = 'static';
      document.body.style.width = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
      document.body.style.position = 'static';
      document.body.style.width = 'auto';
    };
  }, [isPopupOpen]);

  const handleAccountSave = async () => {
    if (!user) return;
    try {
      const userDocRef = doc(db, 'users', user.uid);
      
      // Extract first and last name
      const nameParts = accountDetails.fullName.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      // Update profile data - Phone number goes in UserProfile
      const userProfileRef = doc(userDocRef, 'UserProfile', user.uid);
      const sanitizedPhone = sanitizePhoneNumber(accountDetails.phoneNumber);
      await updateDoc(userProfileRef, {
        firstName,
        lastName,
        phoneNumber: sanitizedPhone,
        phoneCountryCode: accountDetails.phoneCountryCode || '+91',
        updatedAt: serverTimestamp()
      });
      
      // Update personal info - Country goes in PersonalInfo
      const personalInfoRef = doc(userDocRef, 'PersonalInfo', user.uid);
      await updateDoc(personalInfoRef, {
        dob: accountDetails.dob,
        updatedAt: serverTimestamp()
      });
      
      setIsChanged(false);
      toast.success('Account information updated');
    } catch (error) {
      console.error("Error updating account information:", error);
      toast.error('Failed to update account information');
    }
  };

  const handleProfilePhotoUpload = async () => {
    if (!user) return;
    
    const fileInput = document.getElementById('profilePhotoInput');
    const file = fileInput.files[0];
    if (file) {
      try {
        const storageRef = ref(storage, `usersProfilePicture/${user.uid}`);
        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);
        
        setAccountDetails(prev => ({
          ...prev,
          avatar: downloadURL
        }));

        const userDocRef = doc(db, 'users', user.uid);
        const userProfileRef = doc(userDocRef, 'UserProfile', user.uid);
        await updateDoc(userProfileRef, {
          profilePhoto: downloadURL,
        });

        setIsPopupOpen(false);
        toast.success('Profile photo updated');
      } catch (error) {
        console.error('Error uploading profile photo:', error);
        toast.error('Failed to upload profile photo');
      }
    }
  };

  useEffect(() => {
    if (!user) {
      setSubscriptionInfo(null);
      setInvoices([]);
      setSubscriptionLoading(false);
      setInvoicesLoading(false);
      setSubscriptionLimits(null);
      setLimitsLoading(false);
      return;
    }

    let isMounted = true;
    const fetchSubscriptionAndInvoices = async () => {
      setSubscriptionLoading(true);
      setInvoicesLoading(true);
      setLimitsLoading(true);
      try {
        const userDocRef = doc(db, 'users', user.uid);
        const subsRef = collection(userDocRef, 'Subscription');
        const subsSnap = await getDocs(query(subsRef, orderBy('updated_at', 'desc'), limit(1)));

        if (!subsSnap.empty) {
          const subDoc = subsSnap.docs[0];
          const subData = subDoc.data() || {};
          if (isMounted) {
            setSubscriptionInfo({
              id: subDoc.id,
              plan: subData.plan || 'Free',
              status: subData.status || 'active',
              billingCycle: subData.billingCycle || '',
              renewsOn: subData.renews_on || subData.renewsOn || null,
              models: subData.models || [],
              subscriptionId: subData.subscription_id || subData.subscriptionId || null,
            });
          }

          const limitsRef = collection(subDoc.ref, 'Limits');
          const limitsSnap = await getDocs(query(limitsRef, limit(1)));
          const firstLimitDoc = limitsSnap.docs[0];
          if (isMounted) {
            setSubscriptionLimits(firstLimitDoc ? { id: firstLimitDoc.id, ...firstLimitDoc.data() } : null);
          }

          const historyRef = collection(subDoc.ref, 'History');
          const historySnap = await getDocs(query(historyRef, orderBy('synced_at', 'desc')));
          const rows = [];

          for (const histDoc of historySnap.docs) {
            const histData = histDoc.data() || {};
            const invoicesRef = collection(histDoc.ref, 'Invoices');
            const invoicesSnap = await getDocs(query(invoicesRef, orderBy('created_at', 'desc')));
            invoicesSnap.forEach((invoiceDoc) => {
              const invoice = invoiceDoc.data() || {};
              rows.push({
                id: invoiceDoc.id,
                invoiceId: invoice.invoice_id || invoiceDoc.id,
                amount: invoice.amount,
                status: invoice.status || histData.status || 'Paid',
                plan: histData.plan || subData.plan || '—',
                createdAt: invoice.created_at || histData.synced_at || null,
                periodStart: invoice.period_start || histData.period_start || null,
                periodEnd: invoice.period_end || histData.period_end || null,
                url: invoice.url || '',
              });
            });
          }

          rows.sort((a, b) => {
            const dateA = parseToDate(a.createdAt || a.periodEnd);
            const dateB = parseToDate(b.createdAt || b.periodEnd);
            return (dateB?.getTime() || 0) - (dateA?.getTime() || 0);
          });

          if (isMounted) {
            setInvoices(rows);
            setShowAllInvoices(false);
          }
        } else if (isMounted) {
          setSubscriptionInfo(null);
          setInvoices([]);
          setSubscriptionLimits(null);
          setShowAllInvoices(false);
        }
      } catch (error) {
        console.error('Error fetching subscription data:', error);
        if (isMounted) {
          setSubscriptionInfo(null);
          setInvoices([]);
          setSubscriptionLimits(null);
          setShowAllInvoices(false);
        }
      } finally {
        if (isMounted) {
          setSubscriptionLoading(false);
          setInvoicesLoading(false);
          setLimitsLoading(false);
        }
      }
    };

    fetchSubscriptionAndInvoices();
    return () => {
      isMounted = false;
    };
  }, [user]);

  useEffect(() => {
    if (!showAllInvoices && invoices.length <= 6) return;
    if (showAllInvoices && invoices.length <= 6) {
      setShowAllInvoices(false);
    }
  }, [invoices, showAllInvoices]);

  const handleBillingFieldChange = (field, value) => {
    setBillingAddress((prev) => ({ ...prev, [field]: value }));
  };

  const handleBillingSave = async () => {
    if (!user) return;
    setIsSavingBilling(true);
    try {
      const userDocRef = doc(db, 'users', user.uid);
      const userProfileRef = doc(userDocRef, 'UserProfile', user.uid);
      await updateDoc(userProfileRef, {
        address1: billingAddress.line1,
        address2: billingAddress.line2,
        city: billingAddress.city,
        state: billingAddress.state,
        code: billingAddress.code,
        country: billingAddress.country,
        updatedAt: serverTimestamp(),
      });
      setOriginalBilling(billingAddress);
      toast.success('Billing address updated');
    } catch (error) {
      console.error('Failed to update billing address:', error);
      toast.error('Failed to update billing address');
    } finally {
      setIsSavingBilling(false);
    }
  };

  const billingHasChanges = useMemo(() => {
    if (!originalBilling) return false;
    return JSON.stringify(billingAddress) !== JSON.stringify(originalBilling);
  }, [billingAddress, originalBilling]);

  const handleCancelPlan = async () => {
    if (!user || !subscriptionInfo || isCancellingPlan) return;

    const renewalDate = parseToDate(subscriptionInfo.renewsOn);
    if (renewalDate) {
      const diffMs = renewalDate.getTime() - Date.now();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (diffMs > 0 && diffDays > 7) {
        setShowCancelNotice(true);
        return;
      }
    }
    setShowCancelNotice(false);

    if (!subscriptionInfo.subscriptionId) {
      alert('Unable to cancel: subscription identifier missing.');
      return;
    }
    if (!CANCEL_SUBSCRIPTION_API) {
      alert('Cancellation service is unavailable. Please contact support.');
      return;
    }

    setIsCancellingPlan(true);
    try {
      let token = null;
      if (typeof user.getIdToken === 'function') {
        token = await user.getIdToken(true);
      } else if (user?.accessToken) {
        token = user.accessToken;
      } else if (user?.stsTokenManager?.accessToken) {
        token = user.stsTokenManager.accessToken;
      }
      if (!token) {
        throw new Error('Authentication error. Please sign in again.');
      }

      const response = await fetch(CANCEL_SUBSCRIPTION_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ subscription_id: subscriptionInfo.subscriptionId }),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.error || payload?.message || 'Failed to cancel subscription.');
      }

      setSubscriptionInfo((prev) => (prev ? { ...prev, status: 'cancelled' } : prev));
      setShowCancelNotice(false);
      alert('Cancellation requested successfully.');
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      alert(error.message || 'Failed to cancel subscription.');
    } finally {
      setIsCancellingPlan(false);
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return <div className="flex justify-center items-center h-64">
        <div className="loader"></div>
        <p className="ml-2">Loading...</p>
      </div>;
    }

    switch (activeTab) {
      case 'profile':
        return <AccountSection 
                 accountDetails={accountDetails} 
                 setAccountDetails={setAccountDetails}
                 handleSave={handleAccountSave}
                 setIsChanged={setIsChanged}
                 setIsPopupOpen={setIsPopupOpen}
                 isChanged={isChanged} />;
      case 'subscription':
        return (
          <SubscriptionSection
            subscriptionInfo={subscriptionInfo}
            isLoading={subscriptionLoading}
            limits={subscriptionLimits}
            limitsLoading={limitsLoading}
            onCancelPlan={handleCancelPlan}
            cancelLoading={isCancellingPlan}
            showCancelNotice={showCancelNotice}
          />
        );
      case 'billing':
        return (
          <BillingSection
            address={billingAddress}
            onFieldChange={handleBillingFieldChange}
            onSave={handleBillingSave}
            canSave={billingHasChanges}
            isSaving={isSavingBilling}
          />
        );
      case 'invoices':
        return (
          <InvoicesSection
            invoices={invoices}
            isLoading={invoicesLoading}
            showAllInvoices={showAllInvoices}
            onToggleShowAll={() => setShowAllInvoices((prev) => !prev)}
          />
        );
      default:
        return null;
    }
  };

  // Popup for photo upload
  const renderPhotoUploadPopup = () => {
    if (!isPopupOpen) return null;
    
    return (
      <div className='fixed inset-0 flex items-center justify-center bg-black bg-opacity-70 overflow-hidden z-50'>
        <div className='bg-white p-8 rounded-lg shadow-2xl relative border border-gray-300 max-w-md w-full mx-4 sm:mx-0'>
          <button
            type="button"
            className="absolute top-0 right-0 m-4 text-gray-600 hover:text-red-600 transition duration-300 mr-2 mt-2"
            onClick={() => setIsPopupOpen(false)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <h2 className='text-2xl font-semibold mb-6 text-center'>Change Profile Picture</h2>
          <input id="profilePhotoInput" type="file" accept="image/*" className="block w-full text-sm text-gray-900 border-gray-300 cursor-pointer bg-gray-50 focus:outline-none" />
          <div className='flex justify-end mt-6'>
            <button
              type="button"
              className="bg-blue-800 text-white px-4 py-2 rounded-full hover:bg-blue-900 transition duration-300"
              onClick={handleProfilePhotoUpload}
            >
              Upload
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen mt-20 bg-gradient-to-br from-slate-50 via-white to-blue-50" style={layoutShiftStyle}>
      <nav className="sticky top-0 z-10 bg-white/80 backdrop-blur border-b border-gray-200 py-3">
        <div className="container mx-auto px-4 md:px-6">
          <div className="w-full pb-2">
            <div
              className="flex flex-wrap items-center gap-2 sm:gap-3"
              role="tablist"
              aria-label="Settings sections"
            >
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setActiveTab(item.id)}
                    role="tab"
                    aria-selected={isActive}
                    aria-controls={`settings-panel-${item.id}`}
                    className={`group relative flex items-center justify-center gap-2 rounded-full px-3 sm:px-4 py-2 text-sm font-medium transition ${
                      isActive
                        ? 'bg-blue-50 text-blue-800 shadow-sm'
                        : 'text-gray-500 hover:bg-gray-100 hover:text-blue-700'
                    }`}
                  >
                    <Icon className={`text-base transition ${isActive ? 'text-blue-700' : 'text-gray-400 group-hover:text-blue-600'}`} />
                    <span className="whitespace-nowrap">{item.title}</span>
                    {isActive && <span className="absolute bottom-0 inset-x-6 h-1 rounded-full bg-blue-600" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto my-5 p-4 sm:my-8 sm:p-8">
        <main
          id={`settings-panel-${activeTab}`}
          className="w-full rounded-3xl bg-white/90 shadow-xl shadow-blue-50/40 ring-1 ring-slate-100 backdrop-blur"
        >
          <div className="p-4 sm:p-8">{renderContent()}</div>
        </main>
      </div>

      {/* Photo Upload Popup */}
      {renderPhotoUploadPopup()}

      {/* Toasts */}
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover />
    </div>
  );
 };

const AccountSection = ({
  accountDetails,
  setAccountDetails,
  handleSave,
  setIsChanged,
  setIsPopupOpen,
  isChanged,
}) => {
  const countryCodes = useMemo(() => [
    { code: '+91', country: 'India', label: '🇮🇳 +91' },
  ], []);
  const [selectedCountryCode, setSelectedCountryCode] = useState(accountDetails.phoneCountryCode || '+91');
  const [phoneWithoutCode, setPhoneWithoutCode] = useState('');

  useEffect(() => {
    const code = accountDetails.phoneCountryCode || '+91';
    setSelectedCountryCode(code);
    setPhoneWithoutCode(normalizePhoneDigits(accountDetails.phoneNumber, code));
  }, [accountDetails.phoneNumber, accountDetails.phoneCountryCode]);

  const handlePhoneChange = (newPhoneWithoutCode) => {
    const sanitized = sanitizePhoneNumber(newPhoneWithoutCode);
    setPhoneWithoutCode(sanitized);
    setAccountDetails({ ...accountDetails, phoneNumber: sanitized, phoneCountryCode: selectedCountryCode });
    setIsChanged(true);
  };

  const handleCountryCodeChange = (newCode) => {
    setSelectedCountryCode(newCode);
    if (accountDetails.phoneCountryCode !== newCode) {
      setAccountDetails({ ...accountDetails, phoneCountryCode: newCode });
      setIsChanged(true);
    }
  };
 
  return (
    <div className="space-y-10">
     <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h2 className="text-3xl font-semibold text-black py-3">Profile</h2>
      </div>
     </header>

     <section className="overflow-hidden rounded-3xl border border-blue-100 bg-gradient-to-r from-blue-50 via-white to-blue-100/60 shadow-sm">
      <div className="flex flex-col items-center gap-6 p-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-6">
         <div className="relative flex-shrink-0">
          <div className="h-20 w-20 overflow-hidden rounded-full border-4 border-white shadow-lg">
            <img
              src={accountDetails.avatar || 'https://via.placeholder.com/150'}
              alt="Profile"
              className="h-full w-full object-cover"
            />
         </div>
           <button
             type="button"
             onClick={() => setIsPopupOpen(true)}
             className="absolute bottom-0 right-0 flex h-10 w-10 items-center justify-center rounded-full bg-blue-700 text-white shadow transition hover:bg-blue-800"
           >
             <FaCamera />
           </button>
         </div>
         <div>
          <h3 className="text-xl font-semibold text-gray-900">{accountDetails.fullName || 'Your Name'}</h3>
          <p className="text-sm text-gray-600">{accountDetails.email}</p>
         </div>
        </div>
        <div className="flex flex-wrap justify-center gap-3">
         <button
          type="button"
          onClick={() => setIsPopupOpen(true)}
          className="rounded-full border border-blue-200 bg-white px-4 py-2 text-sm font-semibold text-blue-700 shadow-sm transition hover:border-blue-400 hover:text-blue-800"
         >
          Change photo
         </button>
        </div>
      </div>
     </section>

     <section className="space-y-6 mt-10 rounded-3xl border border-gray-100 bg-white/90 p-6 shadow-sm shadow-blue-50/40 backdrop-blur">
      <div className="grid gap-6 md:grid-cols-2">
        <label className="flex flex-col space-y-2 text-sm font-medium text-gray-700">
         <span>Full Name</span>
         <input
          type="text"
          value={accountDetails.fullName}
          onChange={(e) => {
            setAccountDetails({ ...accountDetails, fullName: e.target.value });
            setIsChanged(true);
          }}
          placeholder="Enter your full name"
          className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-gray-700 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
         />
        </label>
        <div className="flex min-w-0 flex-col space-y-2 text-sm font-medium text-gray-700">
         <span>Date of Birth</span>
         <input
           type="date"
           value={accountDetails.dob}
           onChange={(e) => {
            setAccountDetails({ ...accountDetails, dob: e.target.value });
            setIsChanged(true);
           }}
           className="w-full min-w-0 max-w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-gray-700 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
         />
        </div>
        <div className="flex flex-col space-y-2 text-sm font-medium text-gray-700">
         <span>Email Address</span>
         <div className="rounded-2xl border border-gray-200 bg-gray-100 px-4 py-3 text-gray-500 shadow-sm">
          {accountDetails.email}
         </div>
        </div>
        <div className="flex flex-col space-y-2 text-sm font-medium text-gray-700">
         <span>Phone Number</span>
         <div className="flex w-full gap-2">
          <select
            value={selectedCountryCode}
            onChange={(e) => handleCountryCodeChange(e.target.value)}
            className="w-24 sm:w-28 rounded-2xl border border-gray-200 bg-white px-3 py-3 text-gray-700 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
          >
            {countryCodes.map((country) => (
              <option key={country.code} value={country.code}>
                {country.label}
              </option>
            ))}
          </select>
          <input
            type="tel"
            value={phoneWithoutCode}
            onChange={(e) => handlePhoneChange(e.target.value)}
            placeholder="Enter 10-digit phone number"
            maxLength={10}
            className="flex-1 min-w-0 rounded-2xl border border-gray-200 bg-white px-4 py-3 text-gray-700 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
          />
         </div>
        </div>
      </div>
      <div className="flex justify-end">
        <button
         type="button"
         className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white shadow-sm transition ${
          isChanged
            ? 'bg-blue-700 hover:bg-blue-800'
            : 'bg-black cursor-not-allowed'
         }`}
         onClick={handleSave}
         disabled={!isChanged}
        >
         <FaSave className="text-base" />
         Save changes
        </button>
        </div>
     </section>
    </div>
  );
 };
 
 export default SettingsPage;